import React, { useState, useRef, useEffect } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import useAuthStore from "../store/authStore.jsx";
import { getCookie } from "../utils/cookie.jsx";
import LoadingSpinner from "../components/LoadingSpinner.jsx";

const initState = {
  username: "",
  password: "",
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loginParam, setLoginParam] = useState({ ...initState });
  const [rememberMe, setRememberMe] = useState(false);
  const passwordRef = useRef(null);
  const usernameRef = useRef(null);

  const {
    isAuthenticated,
    loading,
    error,
    publicKey,
    initLogin,
    login,
    clearError,
  } = useAuthStore();

  // 로그인 초기화 (컴포넌트 마운트 시 한 번만)
  useEffect(() => {
    const initializeLogin = async () => {
      try {
        await initLogin();
      } catch (error) {
        // 로그인 초기화 실패 처리
      }
    };

    // publicKey가 없을 때만 초기화
    if (!publicKey) {
      initializeLogin();
    }
  }, [initLogin, publicKey]);

  // 저장된 사용자명 복구
  useEffect(() => {
    const savedUsername = getCookie("savedUsername");
    if (savedUsername) {
      setLoginParam((prev) => ({ ...prev, username: savedUsername }));
      setRememberMe(true);
      if (passwordRef.current) {
        passwordRef.current.focus();
      }
    } else {
      if (usernameRef.current) {
        usernameRef.current.focus();
      }
    }
  }, []);

  // 이미 로그인된 경우 리다이렉트
  if (isAuthenticated) {
    const returnUri = searchParams.get("returnUri");
    return <Navigate to={returnUri || "/admin"} replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoginParam((prev) => ({ ...prev, [name]: value }));
    clearError();
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!loginParam.username.trim()) {
      alert("아이디를 입력하세요.");
      return;
    }

    if (!loginParam.password.trim()) {
      alert("Password를 입력하세요.");
      return;
    }

    // 공개키가 없으면 다시 초기화
    if (!publicKey) {
      try {
        await initLogin();
      } catch (error) {
        alert("보안 초기화에 실패했습니다. 페이지를 새로고침해주세요.");
        return;
      }
    }

    const response = await login({
      username: loginParam.username.trim(),
      password: loginParam.password,
      rememberMe,
    });

    // 로그인 성공 시
    if (response && !response.error) {
      const returnUri = searchParams.get("returnUri");
      navigate(returnUri || "/admin", { replace: true });
    } else {
      // 로그인 실패 시 입력 필드 초기화
      setLoginParam({ ...initState });
      if (usernameRef.current) {
        setTimeout(() => {
          usernameRef.current.focus();
        }, 100);
      }
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-form-wrapper">
          {/* 로고 및 제목 */}
          <div className="login-header">
            <div className="login-logo">📺</div>
            <h1 className="login-title">JDLive</h1>
            <h4 className="login-subtitle">현황판 관리 시스템</h4>
          </div>

          {/* 로그인 폼 */}
          <form className="login-form" onSubmit={handleSubmit}>
            {/* 에러 메시지 */}
            <div className="error-message-container">
              {error && <div className="error-message">{error}</div>}
            </div>

            {/* 공개키 로딩 상태 표시 */}
            {!publicKey && !error && (
              <div className="info-message">보안 초기화 중...</div>
            )}

            {/* ID 입력 */}
            <div className="form-group">
              <input
                id="username"
                name="username"
                type="text"
                required
                className="form-input"
                placeholder="아이디를 입력하세요."
                value={loginParam.username}
                onChange={handleChange}
                ref={usernameRef}
                disabled={loading || !publicKey}
              />
            </div>

            {/* 비밀번호 입력 */}
            <div className="form-group">
              <input
                id="password"
                name="password"
                type="password"
                required
                className="form-input"
                placeholder="Password를 입력하세요."
                value={loginParam.password}
                onChange={handleChange}
                ref={passwordRef}
                disabled={loading || !publicKey}
              />
            </div>

            {/* 계정 저장 체크박스 */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  id="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                  disabled={loading || !publicKey}
                />
                <span className="checkbox-text">계정 저장</span>
              </label>
            </div>

            {/* 로그인 버튼 */}
            <div className="form-group">
              <button
                type="submit"
                className="login-button"
                disabled={loading || !publicKey}
              >
                {loading ? (
                  <div className="button-content">
                    <LoadingSpinner size="small" color="white" />
                    <span>로그인 요청 중...</span>
                  </div>
                ) : !publicKey ? (
                  "보안 초기화 중..."
                ) : (
                  "로그인"
                )}
              </button>
            </div>
          </form>

          {/* 푸터 */}
          <div className="login-footer">
            <p>로그인 계정 및 오류는 관리자에 문의바랍니다.</p>
            <p>&copy;JIRANDATA. All rights reserved</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
